<template>
  <Transition name="slide-in">
    <div
      v-if="isOpen"
      class="fixed right-0 top-0 z-[99999999] flex h-dvh w-full flex-col bg-white md:w-1/2 md:min-w-[700px]"
    >
      <div class="z-10 bg-white px-[22px] pt-24 md:px-72 md:pt-36">
        <div class="flex items-center justify-between">
          <h2 class="typo-headline-4 md:typo-headline-2 uppercase">
            {{ $texts('cart.title', 'Cart') }}
          </h2>
          <button class="cursor-pointer bg-transparent" @click="close">
            <SpriteSymbol name="close" class="h-32 w-32 shrink-0" />
          </button>
        </div>
      </div>

      <div
        class="scrollable-content flex flex-1 flex-col overflow-y-auto px-[22px] pb-24 md:px-72"
      >
        <div class="mt-40">
          <p class="text-base uppercase md:text-2xl">
            {{ $texts('cart.productAdded', 'The Product was added.') }}
          </p>

          <div
            class="mt-10 flex flex-col md:mb-[45px] md:mt-[50px] md:flex-row"
          >
            <div
              class="flex w-full justify-center md:mr-16 md:w-[210px] md:justify-start"
            >
              <img
                :src="imageUrl ?? undefined"
                :alt="variation?.title"
                class="max-h-[159px] object-contain"
              />
            </div>

            <div class="mt-[33px] md:mt-16">
              <p class="mb-16 text-md uppercase md:text-lg">
                {{ variation?.title }}
              </p>

              <div class="mb-8">
                <div class="mb-8 flex">
                  <div class="w-80 text-sm">
                    {{ $texts('cart.reference', 'Reference') }}
                  </div>
                  <div class="text-sm">{{ variation?.sku }}</div>
                </div>

                <div v-if="size" class="mb-8 flex">
                  <div class="w-80 text-sm">
                    {{ $texts('cart.variationSize', 'Size') }}
                  </div>
                  <div class="text-sm">{{ size }}</div>
                </div>

                <div class="mb-8 flex">
                  <div class="w-80 text-sm">
                    {{ $texts('cart.price', 'Price') }}
                  </div>
                  <div class="text-sm">
                    {{ variation?.price?.formatted }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="
            orderItemPromotion && orderItemPromotion.promotion?.fieldCartBanner
          "
        >
          <div class="mt-40 overflow-hidden rounded-[10px]">
            <div class="bg-pink-light p-12 text-center text-xs uppercase">
              {{ orderItemPromotion.promotion.fieldCartBanner }}
            </div>

            <div class="flex flex-col bg-grey-light-03 p-16 md:flex-row">
              <div
                v-if="promotionItemImageUrl"
                class="flex w-full justify-center pr-16 md:mr-16 md:w-[210px] md:pr-0"
              >
                <img
                  :src="promotionItemImageUrl"
                  :alt="promotionItemTitle || ''"
                  class="max-h-[159px] object-cover"
                />
              </div>

              <div class="mt-24 grow md:mt-16">
                <p class="mb-16 text-md uppercase md:text-lg">
                  {{ promotionItemTitle }}
                </p>

                <div class="mb-8">
                  <div v-if="isPriceZero" class="mb-8 flex">
                    <div class="w-80 text-sm"></div>
                    <div class="text-sm text-grey-mid line-through">
                      {{ originalPrice?.formatted }}
                    </div>
                  </div>
                  <div class="mb-8 flex">
                    <div class="w-80 text-sm">
                      {{ $texts('cart.price', 'Price') }}
                    </div>
                    <div class="text-sm">
                      {{ promotionItemPrice?.formatted }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="promotion" class="mt-56">
          <a
            class="flex flex-row items-center rounded-[20px] bg-pink-light transition-colors"
            :class="{ 'hover:bg-pink': !!promotion.link?.uri?.path }"
            :href="promotion.link?.uri?.path || '#'"
            target="_parent"
          >
            <div
              v-if="promotion.image"
              class="flex min-w-[122px] justify-center p-16 md:min-w-[220px]"
            >
              <img
                :src="promotion.image.url?.path"
                :alt="promotion.image.name"
                class="h-80 w-auto rounded-sm object-contain"
              />
            </div>
            <div class="py-16 pr-16">
              <p class="text-[20px] uppercase leading-[1.1] md:text-[24px]">
                {{ promotion.text }}
              </p>
            </div>
          </a>
        </div>
      </div>

      <div
        class="z-10 flex w-full flex-col gap-16 border-t border-t-grey-light-01 bg-white p-32 md:flex-row"
      >
        <button
          v-if="!isMobile"
          class="button flex justify-center md:w-1/2"
          @click="close"
        >
          {{ $texts('cart.continueShopping', 'Continue shopping') }}
        </button>
        <button
          class="button is-primary flex justify-center md:w-1/2"
          @click="toCart"
        >
          {{ $texts('cart.goToCart', 'Go to Cart') }}
          <SpriteSymbol name="arrow-right" class="shrink-0" />
        </button>
      </div>
    </div>
  </Transition>
  <div
    v-if="isOpen"
    class="background-overlay fixed inset-0 z-[99999998]"
    @click="close"
  ></div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'

const { isOpen, close, variation } = useCartFlyin()
const { promotion, orderItemPromotion } = useCart()
const { $texts } = useEasyTexts()
const router = useRouter()
const { isMobile } = useViewport()

// Prevent main page scrolling when fly-in is open
watch(isOpen, (newValue) => {
  if (newValue) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})

const toCart = () => {
  close()
  router.push({ name: 'cart' })
}

const imageUrl = computed(() => {
  if (!variation.value) {
    return null
  }
  if ('fieldImageHorizontal' in variation.value) {
    return variation.value.fieldImageHorizontal || null
  } else if ('image' in variation.value) {
    return variation.value.image || null
  }
  return null
})

const size = computed(() => {
  if (!variation.value) {
    return null
  }
  if ('fieldCaseSize' in variation.value) {
    return variation.value.fieldCaseSize || null
  } else if ('size' in variation.value) {
    return variation.value.size || null
  }

  return null
})

// Order item promotion computed properties
const promotionItemTitle = computed(() => {
  if (!orderItemPromotion.value) {
    return null
  }
  return orderItemPromotion.value.title
})

const promotionItemImageUrl = computed(() => {
  if (!orderItemPromotion.value?.purchasedEntity) {
    return null
  }

  const entity = orderItemPromotion.value.purchasedEntity

  if ('image' in entity && typeof entity.image === 'string') {
    return entity.image
  } else if (
    'fieldImageUrl' in entity &&
    typeof entity.fieldImageUrl === 'string'
  ) {
    return entity.fieldImageUrl
  } else if ('media' in entity && entity.media?.image?.file?.rokka?.hash) {
    return entity.media.image.file.rokka.hash
  }

  return null
})

const promotionItemPrice = computed(() => {
  if (!orderItemPromotion.value) {
    return null
  }
  return orderItemPromotion.value.price
})

const isPriceZero = computed(() => {
  if (!promotionItemPrice.value?.number) {
    return false
  }
  return Number(promotionItemPrice.value.number) === 0
})

const originalPrice = computed(() => {
  if (!isPriceZero.value || !orderItemPromotion.value?.purchasedEntity) {
    return null
  }

  const entity = orderItemPromotion.value.purchasedEntity
  if ('originalPrice' in entity) {
    return entity.originalPrice
  }

  return null
})
</script>

<style scoped>
.slide-in-enter-active,
.slide-in-leave-active {
  transition: transform 0.3s ease !important;
}

.slide-in-enter-from,
.slide-in-leave-to {
  transform: translateX(100%) !important;
}

.slide-in-enter-to,
.slide-in-leave-from {
  transform: translateX(0) !important;
}

.background-overlay {
  backdrop-filter: blur(3px);
  background-color: rgb(0 0 0 / 35%);
}

.scrollable-content {
  -webkit-overflow-scrolling: touch;
}

.line-through {
  text-decoration: line-through;
}
</style>
